import { enqueueFieldSave, runWriteThenRefresh } from './fieldSaveQueue';

/**
 * Sequential save queue for band field edits, modelled after
 * `profileFieldSaveQueue` for user profiles. Each save blocks on the
 * previous one so a flurry of blur events (name, location, description,
 * tags) never races against itself or against the post-save refresh.
 *
 * `saveBandField` performs the actual network call and is supplied by the
 * caller — that's where the per-field routing lives (updateBandName vs.
 * generic updateBand vs. /location PATCH).
 */
export function enqueueBandFieldSave(queueRef, {
    bandId,
    field,
    value,
    saveBandField,
    refreshBand,
    onError,
    onWriteSuccess,
    onRefreshError,
    timeoutMs,
} = {}) {
    if (!queueRef || typeof queueRef !== 'object') {
        return Promise.reject(new Error('Cannot save band field without a queue.'));
    }
    if (!bandId || !field) {
        return Promise.reject(new Error('Cannot save band field without a band id and field name.'));
    }
    if (typeof saveBandField !== 'function') {
        return Promise.reject(new Error('Cannot save band field without a save function.'));
    }

    const context = { bandId, field };
    const successContext = { ...context, value };
    return enqueueFieldSave(queueRef, ({ signal } = {}) => runWriteThenRefresh({
        write: ({ signal: writeSignal } = {}) => saveBandField({
            bandId,
            field,
            value,
            signal: writeSignal,
        }),
        onWriteSuccess: (result) => onWriteSuccess?.(result, successContext),
        refresh: ({ signal: refreshSignal } = {}) => refreshBand?.({ signal: refreshSignal }),
        onWriteError: (error) => onError?.(error, context),
        onRefreshError: (error) => onRefreshError?.(error, context),
        signal,
    }), { timeoutMs });
}
